import ApiClient from "../helpers/ApiClient";
import { getAuthToken } from "../helpers/UserDetails";
import { getSyncMode } from "../config/config";
import axios from "axios";

const LOCAL_BACKEND = "http://localhost:5001";
const CLOUD_BACKEND = "https://ehospitalitynepal.com"; // Production Cloud URL

/**
 * The SyncEngine handles bidirectional synchronization between local Electron
 * and the Cloud SaaS.
 */
class SyncEngine {
  constructor() {
    this.isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
    this.isElectron = typeof navigator !== "undefined" && (navigator.userAgent.toLowerCase().includes('electron') || window.location.port === '5001');
    this.intervalId = null;
    this.isSyncing = false; // Guard to prevent overlapping cycles

    if (typeof window !== "undefined") {
      window.addEventListener('online', () => this.handleOnline());
      window.addEventListener('offline', () => this.handleOffline());
    }
  }

  start() {
    if (!this.isElectron) return; // Only run in Electron (Local-First)
    if (getSyncMode() === 'online_only') {
      console.log("[SyncEngine] Online-Only mode active. SyncEngine paused.");
      return;
    }

    console.log("[SyncEngine] Engine Started in Offline-First mode...");
    
    // Safety: Always try to sync pending changes FIRST before any hydration if online
    if (navigator.onLine) {
      this.performSync();
    }

    // Check for sync every 10 seconds if online
    this.intervalId = setInterval(() => {
      if (navigator.onLine && getSyncMode() === 'offline_sync') {
        this.performSync();
      }
    }, 10000);
  }

  stop() {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  handleOnline() {
    if (getSyncMode() === 'online_only') return;
    console.log("App is back online. Triggering sync...");
    this.performSync();
  }

  handleOffline() {
    console.log("App went offline. Working in Local SQLite Mode.");
  }

  async logRemote(level, message, data = null) {
    try {
      await ApiClient.post("/sync/log", { level, message, data });
    } catch (e) {}
  }

  async performSync() {
    if (this.isSyncing || !navigator.onLine) return;
    if (getSyncMode() === 'online_only') return;

    try {
      this.isSyncing = true;
      const token = getAuthToken();
      if (!token || token === 'null' || token === 'undefined') {
        this.isSyncing = false;
        return;
      }

      if (typeof window.require === 'function') {
        const electron = window.require('electron');
        if (electron && electron.ipcRenderer) {
          electron.ipcRenderer.send('set-auth-token', token);
        }
      }

      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const tenantId = userDetails?.tenant_id;

      // 1. Fetch pending changes from Local Backend
      const response = await ApiClient.get("/sync/pending");
      const pendingChanges = response.data;

      if (pendingChanges.length > 0) {
        this.logRemote("info", `Pushing ${pendingChanges.length} changes to Cloud (Batched)...`);
        
        try {
          const cloudResponse = await axios.post(`${CLOUD_BACKEND}/api/v1/sync/ingest`, {
            tenantId: tenantId,
            changes: pendingChanges // Send all changes in one batch
          }, {
            headers: { 
              'Authorization': `Bearer ${token}` 
            },
            withCredentials: false
          });

          if (cloudResponse.status === 200 && cloudResponse.data.syncedIds?.length > 0) {
            const remoteIds = cloudResponse.data.syncedIds;
            await ApiClient.post("/sync/mark-synced", { ids: remoteIds });
            this.logRemote("success", `Successfully pushed ${remoteIds.length} changes in batch.`);
          }
        } catch (cloudError) {
          const errorMsg = cloudError.response?.data?.message || cloudError.message;
          this.logRemote("error", `Failed to sync batch of ${pendingChanges.length} changes: ${errorMsg}`);
        }
      }

      // 2. Hydrate from Cloud to Local on initial setup (v2 includes variants, addons, recipes)
      const initialHydrationDone = localStorage.getItem("initial_hydration_v2");
      if (!initialHydrationDone) {
        await this.hydrateLocalData();
        localStorage.setItem("initial_hydration_v2", "true");
      }

    } catch (error) {
      this.logRemote("error", `Sync Cycle Failed: ${error.message}`);
    } finally {
      this.isSyncing = false;
    }
  }

  async hydrateLocalData() {
    if (!navigator.onLine || getSyncMode() === 'online_only') return;
    try {
      const token = getAuthToken();
      if (!token || token === 'null' || token === 'undefined') return;

      const pendingResponse = await ApiClient.get("/sync/pending");
      if (pendingResponse.data && pendingResponse.data.length > 0) return;

      this.logRemote("info", "Hydrating local data from cloud...");
      
      const cloudDataResponse = await axios.get(`${CLOUD_BACKEND}/api/v1/pos/init?fullSync=true`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });
      
      const cloudData = cloudDataResponse.data;

      await ApiClient.post("/sync/hydrate", {
        categories: cloudData.categories,
        menuItems: cloudData.menuItems,
        users: cloudData.users,
        activeOrders: cloudData.activeOrders || [],
        activeOrderItems: cloudData.activeOrderItems || [],
        paymentTypes: cloudData.paymentTypes || [],
        storeTables: cloudData.storeTables || [],
        taxes: cloudData.taxes || [],
        printSettings: cloudData.printSettings ? [cloudData.printSettings] : [],
        loyaltySettings: cloudData.loyaltySettings ? [cloudData.loyaltySettings] : [],
        storeDetails: cloudData.storeSettings ? [cloudData.storeSettings] : [],
        storeSettings: cloudData.storeSettings || null,
        invoices: cloudData.invoices || [],
        customers: cloudData.customers || [],
        suppliers: cloudData.suppliers || [],
        inventoryItems: cloudData.inventoryItems || [],
        purchases: cloudData.purchases || [],
        purchaseItems: cloudData.purchaseItems || [],
        productUnitConversions: cloudData.productUnitConversions || [],
        purchaseUnits: cloudData.purchaseUnits || [],
        variants: cloudData.variants || [],
        addons: cloudData.addons || [],
        recipeItems: cloudData.recipeItems || []
      });

      this.logRemote("success", "Hydration complete.");
    } catch (error) {
      this.logRemote("error", `Hydration Failed: ${error.message}`);
    }
  }

  async cacheImages(menuItems) {
    try {
      if (typeof window.require !== 'function') return;
      const electron = window.require('electron');
      if (!electron || !electron.ipcRenderer) return;
      
      const { ipcRenderer } = electron;
      for (const item of menuItems) {
        if (item.image) {
          const url = `${CLOUD_BACKEND}/public/${item.image}`;
          const filename = item.image.split('/').pop();
          await ipcRenderer.invoke('cache-image', { url, filename });
        }
      }
    } catch (e) {
      console.error("Image caching failed:", e);
    }
  }
}

export default new SyncEngine();
